import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { doc, runTransaction } from 'firebase/firestore'
import {
  CheckCircle2, XCircle, ArrowLeft, ArrowRight, Trophy, RotateCcw,
} from 'lucide-react'
import { useProgress } from '../context/ProgressContext'
import { useAuth } from '../context/AuthContext'
import { db, APP_COLLECTION, APP_DOC } from '../lib/firebase'
import { getModule } from '../data/modules'
import { courseHref } from '../data/courses'
import {
  MAX_ATTEMPTS, isResolved, flattenQuestions, questionIndexesForPage,
  isPageComplete, buildQuestionResult, aggregateResults, recordForFinishedRun, validateQuestions,
  capAttemptHistory, canJumpToPage,
} from '../lib/lessonScoring'
import { lessonScreen, summaryFromProgress, isLocalRunStale } from '../lib/completionTruth'
import { contentFingerprint, packRun, unpackRun } from '../lib/runState'
import { scopedKey, clearLegacyKey } from '../lib/localKeys'

const LS_PREFIX = 'lesson-state'

/** Attempts retained per module. Each one embeds every question asked. */
const MAX_HISTORY = 10

/**
 * A lesson taken one point at a time: each page is a single idea followed by
 * a short check on that idea. You can't move on until the questions on the
 * page are settled — either answered correctly or out of attempts.
 *
 * Why not reuse Quiz: Quiz is one pool of questions at the end of a page,
 * samples five of them, and owns module completion. Here the questions are
 * bound to the point they test and all of them are asked. What the two do
 * share is the shape of what gets stored — this writes exactly the
 * quizResults/attemptHistory that Quiz does, so the admin console, the
 * per-question drill-down and the reissue flow need no changes and don't have
 * to care which kind of module produced the record.
 *
 * pages: [{ title, content: <JSX>, questions: [{ question, options, correctIndex, explanation }] }]
 */
export default function PagedLesson({ moduleId, pages = [], passingScore = 0.7 }) {
  const { completeModule, startModule, getReissuedModules, getModuleProgress, loading } = useProgress()
  const { user } = useAuth()
  const navigate = useNavigate()

  const allQuestions = flattenQuestions(pages)
  const total = allQuestions.length

  // A read-through: no questions, completes without a score. Module 17 is one,
  // since its steps now live in the Confluence guides.
  //
  // The module has to DECLARE it. Reading it off "this lesson has no questions"
  // fails open — a lesson that lost its questions in a bad edit would quietly
  // start recording completions with no score, no answers and no attempt
  // history, and be excluded from the average rather than showing as failed.
  // Declared up here because the staleness check below needs it.
  const readThrough = getModule(moduleId)?.readThrough === true
  const { unscored, undeclared } = recordForFinishedRun([], allQuestions.length, undefined, { readThrough })

  // A malformed question marks a correct answer wrong, and the trainee has no
  // way to tell that's our fault rather than theirs. Say so on the page.
  //
  // A lesson with no questions that never claimed to be a read-through is the
  // same class of fault, and is reported the same way rather than completing:
  // finishing it would write a completion nobody can account for.
  const problems = [
    ...validateQuestions(pages),
    ...(undeclared ? ['This lesson has no questions and is not marked as a read-through.'] : []),
  ]

  const lsKey = scopedKey(LS_PREFIX, user?.uid, moduleId)

  // Fingerprint of the lesson as it stands now. A stored run that was written
  // against different content is discarded rather than replayed against
  // questions that have since moved.
  const fingerprint = useMemo(() => contentFingerprint(pages), [pages])

  const saved = (() => {
    try {
      return unpackRun(localStorage.getItem(lsKey), { uid: user?.uid, fingerprint })
    } catch { return null }
  })()

  const [pageIndex, setPageIndex] = useState(saved?.pageIndex || 0)
  const [results, setResults] = useState(saved?.results || [])
  // What THIS page session scored, if it finished a run here. It is the freshest
  // and most complete record while the session lasts — `completeModule` does not
  // put the score into the context, and the answers behind it go to Firestore in
  // a transaction of their own — so the completion screen prefers it and falls
  // back to the server's record on a later visit.
  const [sessionSummary, setSessionSummary] = useState(null)
  // The run finished but the record of it didn't reach Firestore.
  const [saveFailed, setSaveFailed] = useState(false)
  // The trainee has asked to take it again. Restored from the stored run,
  // because a retake that lives only in page-session state is lost on a reload —
  // which dropped them back on their old result screen, whose only way into the
  // lesson deletes the answers they had just given. See `lessonScreen` for why
  // it has to outrank the server's completion.
  const [retaking, setRetaking] = useState(saved?.retaking === true)
  // Bumped on reset/retake so the timing effect below re-stamps its clocks
  // even when the page index was already 0.
  const [runId, setRunId] = useState(0)
  // When the stored answers were last written, so they can be compared against
  // an admin resetting the module. Answers mid-run count as much as a finished
  // one: a reset means start again, whatever stage they had reached.
  const savedAtRef = useRef(saved?.savedAt || 0)
  const topRef = useRef(null)
  const questionStartRef = useRef({})

  /**
   * What the server says about this module, and therefore which screen to draw.
   *
   * Completion is read from here and nowhere else — see
   * `docs/completion-truth.md`. localStorage holds answers, never a verdict, so
   * a run that failed to save cannot come back after a reload claiming to be
   * complete, and a reset or a reissue needs no marker to be noticed: both
   * delete `completed`, which is the only field this consults.
   */
  const serverProgress = getModuleProgress(moduleId)
  const screen = lessonScreen({ loading, progress: serverProgress, justFinished: sessionSummary !== null, retaking })
  const summary = sessionSummary ?? summaryFromProgress(serverProgress, { total, passingScore, unscored })

  // startModule's identity changes on every progress write, so an unguarded
  // effect re-fires and can overwrite the original startedAt that
  // completeModule measures time-spent from. Quiz.jsx guards it the same way.
  const startedRef = useRef(false)
  useEffect(() => {
    if (startedRef.current) return
    startedRef.current = true
    startModule(moduleId)
  }, [moduleId, startModule])

  // Drop the pre-uid key, so a shared browser stops showing the next trainee
  // the previous one's answers.
  useEffect(() => { clearLegacyKey(LS_PREFIX, moduleId) }, [moduleId])

  /** Throw away the answers held in this browser and start the lesson over. */
  const clearRun = useCallback(() => {
    try { localStorage.removeItem(lsKey) } catch { /* private mode */ }
    questionStartRef.current = {}
    setResults([]); setPageIndex(0); setSessionSummary(null)
    savedAtRef.current = 0
    setSaveFailed(false)
    setRetaking(false)
    setRunId((n) => n + 1)
  }, [lsKey])

  /**
   * Answers an admin has invalidated are dropped once the server's record is in.
   *
   * This is all that is left of the old staleness check. It no longer has to
   * infer anything about completion — a reset or a reissue deletes `completed`,
   * so the screen follows by itself — and it concerns only whether the trainee's
   * half-finished answers still stand. Gated on `loading` so an empty record is
   * never mistaken for a wipe.
   */
  const staleCheckedRef = useRef(false)
  useEffect(() => {
    if (staleCheckedRef.current || loading) return
    staleCheckedRef.current = true
    // This cannot be a derivation: `pageIndex` and `results` are seeded from
    // localStorage during the first render, before the server's record is in,
    // so discarding them is a state transition rather than something render can
    // compute. The ref above makes it one-shot, so it cascades a single render.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (isLocalRunStale(serverProgress, savedAtRef.current)) clearRun()
  }, [loading, serverProgress, clearRun])

  const persist = useCallback((next) => {
    try {
      const at = Date.now()
      savedAtRef.current = at
      localStorage.setItem(lsKey, packRun(next, { uid: user?.uid, fingerprint, at }))
    } catch { /* private mode — the run still works, it just won't resume */ }
  }, [lsKey, user?.uid, fingerprint])

  // Start each question's clock when its page becomes visible. This used to be
  // done inline during render, which both broke the render-purity rule and
  // meant the clock was never restarted.
  const stampClocks = useCallback((idxs) => {
    const now = Date.now()
    for (const idx of idxs) {
      if (!questionStartRef.current[idx]) questionStartRef.current[idx] = now
    }
  }, [])

  useEffect(() => {
    stampClocks(questionIndexesForPage(pages, pageIndex))
  }, [pages, pageIndex, runId, stampClocks])

  const page = pages[pageIndex]
  const pageQuestionIdxs = questionIndexesForPage(pages, pageIndex)
  const canAdvance = isPageComplete(pages, pageIndex, results)
  const isLastPage = pageIndex === pages.length - 1

  const [subQuestionIndex, setSubQuestionIndex] = useState(0)

  // Reset or focus first unresolved sub-question when pageIndex changes or question list changes
  useEffect(() => {
    if (pageQuestionIdxs.length === 0) {
      setSubQuestionIndex(0)
      return
    }
    const firstUnresolved = pageQuestionIdxs.findIndex((globalIdx) => !isResolved(results[globalIdx]))
    setSubQuestionIndex(firstUnresolved >= 0 ? firstUnresolved : 0)
  }, [pageIndex, pageQuestionIdxs.length])


  const handleAnswer = useCallback((question, optionIndex) => {
    const existing = results[question.questionIndex]
    if (isResolved(existing)) return

    const attempts = (existing?.attempts || 0) + 1
    const correct = optionIndex === question.correctIndex
    const started = questionStartRef.current[question.questionIndex]
    const timeMs = started ? Date.now() - started : 0

    const record = buildQuestionResult(question, {
      attempts,
      correct,
      timeMs: (existing?.timeMs || 0) + timeMs,
      selectedAnswers: [...(existing?.selectedAnswers || []), optionIndex],
    })

    // Restart this question's clock. Without it the next attempt is measured
    // from first render again and added to what is already stored, so a second
    // attempt 15s later turned 25s of work into 35s.
    questionStartRef.current[question.questionIndex] = Date.now()

    const next = [...results]
    next[question.questionIndex] = record
    setResults(next)
    persist({ pageIndex, results: next, retaking })
  }, [results, pageIndex, retaking, persist])

  const goTo = useCallback((idx) => {
    setPageIndex(idx)
    persist({ pageIndex: idx, results, retaking })
    topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [results, retaking, persist])

  const handleFinish = useCallback(async () => {
    const agg = aggregateResults(results, total, passingScore)
    const completedAt = new Date().toISOString()
    const record = {
      questions: results.filter(Boolean),
      totalScore: agg.totalScore,
      maxPossibleScore: agg.maxPossibleScore,
      percentageScore: agg.percentageScore,
      completedAt,
    }

    // The session's own record of the run. Nothing about finishing is written
    // to localStorage: this state is what draws the completion screen for as
    // long as the session lasts, and after that the server's record is. That is
    // the whole point — a run whose write never landed is resumable on the next
    // load instead of coming back as a completion nobody recorded.
    setSessionSummary(agg)
    persist({ pageIndex, results })
    topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })

    if (!user?.uid) return

    // Nothing to store but the completion itself.
    if (unscored) {
      const done = await completeModule(moduleId)
      setSaveFailed(!done.ok)
      return
    }

    // One transaction, so two tabs finishing at once can't both read history
    // length N and both write attempt N+1 — which silently lost an attempt and
    // left retryCount wrong. The history is capped because every entry carries
    // the full question text; unbounded, it eventually hits Firestore's 1 MiB
    // document limit, after which EVERY write for that module fails.
    let scoreWritten = false
    try {
      const docRef = doc(db, APP_COLLECTION, APP_DOC, 'users', user.uid, 'progress', String(moduleId))
      await runTransaction(db, async (tx) => {
        const snap = await tx.get(docRef)
        const existing = snap.exists() ? snap.data() : {}
        const history = capAttemptHistory([
          ...(existing.attemptHistory || []),
          {
            questions: record.questions,
            totalScore: agg.totalScore,
            maxPossibleScore: agg.maxPossibleScore,
            percentageScore: agg.percentageScore,
            passed: agg.passed,
            completedAt,
          },
        ], MAX_HISTORY)
        tx.set(docRef, {
          quizResults: { ...record, kind: 'paged-lesson', retryCount: history.length - 1 },
          attemptHistory: history,
        }, { merge: true })
      })
      scoreWritten = true
    } catch (err) {
      console.error('[PagedLesson] could not record the attempt:', err)
    }

    // Only tick the module off once the score and answers behind it are stored.
    // Completing regardless produced a progress doc with completed: true, no
    // quizResults and no attemptHistory: ticked off in the admin console with a
    // blank score, no drill-down, and silently excluded from the average.
    const done = scoreWritten ? await completeModule(moduleId, agg.percentageScore) : { ok: false }
    setSaveFailed(!done.ok)
  }, [results, total, unscored, passingScore, pageIndex, user?.uid, moduleId, completeModule, persist])

  const handleRetake = useCallback(() => {
    // clearRun clears questionStartRef too. Leaving it in place meant the first
    // answer of a retake counted every second since the page was first
    // rendered — potentially hours.
    clearRun()
    // Without this the server's completion redraws the screen they just left
    // and the button appears to do nothing. clearRun cleared it, so this has to
    // come after.
    setRetaking(true)
    // Written now rather than on the first answer, so a trainee who reloads
    // before answering anything is still in the retake instead of back on the
    // result screen they deliberately left.
    persist({ pageIndex: 0, results: [], retaking: true })
    topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [clearRun, persist])

  const leave = useCallback(() => {
    const pending = getReissuedModules().filter((id) => id !== moduleId)
    if (pending.length > 0) { navigate(`/module/${pending[0]}`); return }
    const track = getModule(moduleId)?.track
    navigate(track ? courseHref(track) : '/')
  }, [getReissuedModules, moduleId, navigate])

  /* ─── Waiting ─── */

  // Neither screen is drawn until the server's record is in. Deciding early
  // rendered the lesson and then corrected itself, which is the flash
  // `docs/completion-truth.md` calls C6 — and it is what made a finished
  // read-through look like a slow page rather than a bug.
  if (screen === 'waiting') {
    return (
      <div className="flex min-h-48 items-center justify-center">
        <div className="h-6 w-6 animate-spin rounded-full border-4 border-indigo-500 border-t-transparent" />
      </div>
    )
  }

  /* ─── Finished ─── */

  if (screen === 'finished') {
    // A scored module can be completed with no score stored behind it: a legacy
    // document (KNOWN-ISSUES 5). Showing 0% there would invent a failure the
    // trainee never earned, so the score line is simply left out.
    const showScore = !unscored && summary != null
    const pct = showScore ? Math.round(summary.percentageScore * 100) : null
    const wellDone = unscored || summary == null || summary.passed
    return (
      <div ref={topRef} className="rounded-xl border-2 border-slate-200 bg-white p-8 text-center">
        <Trophy className={`mx-auto mb-3 h-10 w-10 ${wellDone ? 'text-amber-400' : 'text-slate-300'}`} />
        <h2 className="text-2xl font-bold text-slate-800">
          {wellDone ? 'Module complete' : 'Finished — worth another look'}
        </h2>
        <p className="mt-2 text-slate-600">
          {unscored
            ? 'Nothing to score here — this one is a read-through, and it is marked as done.'
            : showScore
              // The denominator comes from the SUMMARY, not from the lesson as
              // it stands now. `correctCount` is a stored number; pairing it
              // with the live question count reads as "7 of 5 questions right"
              // the moment anyone edits the lesson. Both halves have to come
              // from the same run.
              ? <>You got {summary.correctCount} of {summary.maxPossibleScore} questions right, scoring <strong>{pct}%</strong>.</>
              : 'This one is marked as done. The score behind it was not kept.'}
        </p>
        {showScore && !summary.passed && (
          <p className="mx-auto mt-3 max-w-md text-sm text-slate-500">
            Below 70%. Nothing is held against you — read it through again and retake it whenever
            you like.
          </p>
        )}
        {saveFailed && (
          <p className="mx-auto mt-3 max-w-md rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            {unscored
              // Not "come back later". A read-through stores nothing but its
              // completion, so a write that did not land leaves the server with
              // no record at all — and by design the next load then draws the
              // lesson rather than this screen. Finishing it again is the only
              // thing that retries the write, and the button below is right
              // here, so say so.
              ? <>We couldn't record this — you may be offline. Press <strong>Read it again</strong> and finish it once more, otherwise the module will show as incomplete. Leaving this page now loses it.</>
              : <>We couldn't save this result — you may be offline. Your answers are still on this device, so press <strong>Take it again</strong> once you're back online, otherwise the module will show as incomplete.</>}
          </p>
        )}
        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
          <button
            onClick={leave}
            className="rounded-lg bg-slate-800 px-5 py-2.5 text-sm font-semibold text-white hover:bg-slate-700"
          >
            Back to the course
          </button>
          <button
            onClick={handleRetake}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-5 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50"
          >
            <RotateCcw className="h-4 w-4" />
            {unscored ? 'Read it again' : 'Take it again'}
          </button>
        </div>
      </div>
    )
  }

  if (!page) return null

  /* ─── Reading ─── */

  return (
    <div ref={topRef} className="space-y-6">
      {problems.length > 0 && (
        <div className="rounded-lg border-2 border-red-300 bg-red-50 p-4">
          <p className="text-sm font-semibold text-red-800">
            Some questions in this module are set up wrongly.
          </p>
          <p className="mt-1 text-sm text-red-700">
            This is a fault in the course, not in your answers. Please tell your trainer and
            mention this module.
          </p>
          <ul className="mt-2 space-y-0.5 text-xs text-red-600">
            {problems.map((p) => <li key={p}>{p}</li>)}
          </ul>
        </div>
      )}

      {/* Progress */}
      <div>
        <div className="mb-2 flex items-center justify-between text-sm">
          <span className="font-medium text-slate-500">
            Point {pageIndex + 1} of {pages.length}
          </span>
          {!unscored && (
            <span className="text-slate-400">
              {results.filter(Boolean).length} of {total} questions answered
            </span>
          )}
        </div>
        <div className="flex gap-1">
          {pages.map((p, i) => (
            <button
              key={i}
              onClick={() => (canJumpToPage(pages, i, results, pageIndex) ? goTo(i) : null)}
              disabled={!canJumpToPage(pages, i, results, pageIndex)}
              title={p.title}
              className={`h-1.5 flex-1 rounded-full transition-colors ${
                i === pageIndex
                  ? 'bg-indigo-500'
                  : isPageComplete(pages, i, results) && i < pageIndex
                    ? 'bg-indigo-300 hover:bg-indigo-400'
                    : 'bg-slate-200'
              } ${i <= pageIndex ? 'cursor-pointer' : 'cursor-default'}`}
            />
          ))}
        </div>
      </div>

      {/* The point */}
      <AnimatePresence mode="wait">
        <motion.section
          key={pageIndex}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.2 }}
        >
          <h2 className="mb-4 text-2xl font-bold text-slate-800">{page.title}</h2>
          <div className="space-y-3 text-slate-600">{page.content}</div>

          {/* The check on that point */}
          {pageQuestionIdxs.length > 0 && (
            <div className="mt-8 space-y-4">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                    Quick check
                  </span>
                  {pageQuestionIdxs.length > 1 && (
                    <span className="rounded-full bg-indigo-50 px-2.5 py-0.5 text-xs font-semibold text-indigo-700 border border-indigo-100">
                      Check {subQuestionIndex + 1} of {pageQuestionIdxs.length}
                    </span>
                  )}
                </div>
                <div className="h-px flex-1 bg-slate-200" />

                {/* Sub-question pagination pills when multiple questions exist */}
                {pageQuestionIdxs.length > 1 && (
                  <div className="flex items-center gap-1.5">
                    {pageQuestionIdxs.map((globalIdx, localIdx) => {
                      const res = results[globalIdx]
                      const resolved = isResolved(res)
                      const isCurrent = localIdx === subQuestionIndex
                      return (
                        <button
                          key={localIdx}
                          type="button"
                          onClick={() => setSubQuestionIndex(localIdx)}
                          className={`inline-flex items-center justify-center rounded-lg px-2.5 py-1 text-xs font-medium transition-all ${
                            isCurrent
                              ? 'bg-indigo-600 text-white shadow-xs ring-2 ring-indigo-300'
                              : resolved && res?.correct
                                ? 'bg-green-50 text-green-700 border border-green-200 hover:bg-green-100'
                                : resolved
                                  ? 'bg-slate-100 text-slate-500 border border-slate-200 hover:bg-slate-200'
                                  : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
                          }`}
                          title={`Question ${localIdx + 1}${resolved ? (res?.correct ? ' (Correct)' : ' (Completed)') : ''}`}
                        >
                          {resolved && res?.correct ? (
                            <CheckCircle2 className="mr-1 h-3.5 w-3.5 text-green-600" />
                          ) : null}
                          Q{localIdx + 1}
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>

              {/* Render active paginated question */}
              {(() => {
                const activeGlobalIdx = pageQuestionIdxs[pageQuestionIdxs.length > 1 ? subQuestionIndex : 0]
                if (activeGlobalIdx === undefined) return null

                const question = allQuestions[activeGlobalIdx]
                const result = results[activeGlobalIdx]
                const resolved = isResolved(result)
                const attemptsUsed = result?.attempts || 0
                const hasNextSub = pageQuestionIdxs.length > 1 && subQuestionIndex < pageQuestionIdxs.length - 1
                const hasPrevSub = pageQuestionIdxs.length > 1 && subQuestionIndex > 0

                return (
                  <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-xs">
                    {pageQuestionIdxs.length > 1 && (
                      <div className="mb-2 flex items-center justify-between text-xs font-semibold text-slate-400">
                        <span>QUESTION {subQuestionIndex + 1} OF {pageQuestionIdxs.length}</span>
                        {resolved && (
                          <span className={result.correct ? 'text-green-600 font-medium' : 'text-slate-500'}>
                            {result.correct ? '✓ Correct' : 'Completed'}
                          </span>
                        )}
                      </div>
                    )}
                    <p className="mb-3 font-medium text-slate-800">{question.question}</p>
                    <div className="space-y-2">
                      {question.options.map((opt, oi) => {
                        const picked = result?.selectedAnswers?.includes(oi)
                        const isCorrect = oi === question.correctIndex
                        let style = 'border-slate-200 hover:border-indigo-300 hover:bg-indigo-50/40'
                        if (resolved && isCorrect) {
                          style = 'border-green-400 bg-green-50 text-green-900'
                        } else if (picked && !isCorrect) {
                          style = 'border-red-300 bg-red-50 text-red-800'
                        } else if (resolved) {
                          style = 'border-slate-200 bg-slate-50 text-slate-400'
                        }
                        return (
                          <button
                            key={oi}
                            onClick={() => handleAnswer(question, oi)}
                            disabled={resolved || picked}
                            className={`flex w-full items-start gap-2.5 rounded-lg border px-4 py-2.5 text-left text-sm transition-colors ${style} ${
                              resolved || picked ? 'cursor-default' : 'cursor-pointer'
                            }`}
                          >
                            {resolved && isCorrect && <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-green-600" />}
                            {picked && !isCorrect && <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />}
                            <span>{opt}</span>
                          </button>
                        )
                      })}
                    </div>

                    {!resolved && attemptsUsed > 0 && (
                      <p className="mt-3 text-sm text-amber-700">
                        Not quite — {MAX_ATTEMPTS - attemptsUsed} attempt
                        {MAX_ATTEMPTS - attemptsUsed === 1 ? '' : 's'} left. Have another go.
                      </p>
                    )}

                    {resolved && (
                      <div
                        className={`mt-3 rounded-lg px-4 py-3 text-sm ${
                          result.correct ? 'bg-green-50 text-green-900' : 'bg-slate-100 text-slate-700'
                        }`}
                      >
                        <p className="font-semibold">
                          {result.correct
                            ? attemptsUsed === 1 ? 'Correct.' : `Correct — on attempt ${attemptsUsed}.`
                            : 'The answer is highlighted above.'}
                        </p>
                        <p className="mt-1">{question.explanation}</p>
                      </div>
                    )}

                    {/* Sub-pagination navigation footer if multiple questions exist */}
                    {pageQuestionIdxs.length > 1 && (
                      <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-3 text-xs">
                        <button
                          type="button"
                          onClick={() => setSubQuestionIndex((prev) => Math.max(0, prev - 1))}
                          disabled={!hasPrevSub}
                          className="inline-flex items-center gap-1 font-medium text-slate-500 hover:text-slate-800 disabled:opacity-30 disabled:hover:text-slate-500"
                        >
                          <ArrowLeft className="h-3.5 w-3.5" />
                          Previous check
                        </button>

                        <div className="flex items-center gap-2">
                          {hasNextSub ? (
                            <button
                              type="button"
                              onClick={() => setSubQuestionIndex((prev) => Math.min(pageQuestionIdxs.length - 1, prev + 1))}
                              className="inline-flex items-center gap-1 rounded-md bg-indigo-600 px-3 py-1.5 font-medium text-white hover:bg-indigo-700 transition-colors"
                            >
                              Next check
                              <ArrowRight className="h-3.5 w-3.5" />
                            </button>
                          ) : isPageComplete(pages, pageIndex, results) ? (
                            <span className="font-medium text-green-700">
                              ✓ All checks completed for this section
                            </span>
                          ) : (
                            <span className="text-slate-400">
                              Complete all checks to proceed
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )
              })()}
            </div>
          )}
        </motion.section>
      </AnimatePresence>

      {/* Navigation */}
      <div className="flex items-center justify-between border-t border-slate-200 pt-5">
        <button
          onClick={() => goTo(Math.max(0, pageIndex - 1))}
          disabled={pageIndex === 0}
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 disabled:cursor-not-allowed disabled:text-slate-300"
        >
          <ArrowLeft className="h-4 w-4" />
          Previous
        </button>

        {!canAdvance ? (
          <span className="text-sm text-slate-400">
            Answer the check above to continue
          </span>
        ) : isLastPage ? (
          // `undeclared` is a property of the LESSON, not of this page, so it
          // has to be tested inside the last-page branch. Ahead of it, it
          // matched on every page and took away the "Next point" button — which
          // stopped the trainee READING a lesson whose only fault was ours.
          undeclared ? (
            <span className="text-sm text-red-500">
              Can't be completed until the fault above is fixed
            </span>
          ) : (
            <button
              onClick={handleFinish}
              className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700"
            >
              Finish module
              <ArrowRight className="h-4 w-4" />
            </button>
          )
        ) : (
          <button
            onClick={() => goTo(pageIndex + 1)}
            className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700"
          >
            Next point
            <ArrowRight className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  )
}
